import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { Switch } from "../ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const GOLD = "#C9A84C";
const PHASES = [{ n: 1, label: "Phase 1 · Platform Access", amount: "₹25,000" }, { n: 2, label: "Phase 2 · API & Full Activation", amount: "₹53,000" }];

function fmt(iso) { return iso ? new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""; }

// Admin-side: a "Waive Fee" toggle per phase. Once waived the switch locks; undo needs a second explicit admin action with a reason.
export function FeeWaiverControls({ api, director, phases, onChanged }) {
  const [dlg, setDlg] = useState(null); // { phase, mode: 'waive'|'revoke' }
  const [confirm, setConfirm] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  useEffect(() => { setLog(null); }, [director.id]);
  const loadLog = async () => { try { const r = await api.get(`/admin/directors/${director.id}/vcb-fee-waiver`); setLog(r.data.log); } catch { setLog([]); } };

  const openDlg = (phase, mode) => { setDlg({ phase, mode }); setConfirm(false); setNote(""); };
  const submit = async () => {
    setBusy(true);
    try {
      const url = `/admin/directors/${director.id}/vcb-fee-waiver/${dlg.phase}${dlg.mode === "revoke" ? "/revoke" : ""}`;
      const body = dlg.mode === "revoke" ? { confirm, reason: note } : { confirm, note };
      const r = await api.post(url, body);
      onChanged?.(r.data.journey);
      toast.success(dlg.mode === "revoke" ? `Phase ${dlg.phase} waiver revoked` : `Phase ${dlg.phase} fee waived`, { description: `Logged ${fmt(r.data.record.at)} · ${r.data.record.by}` });
      setDlg(null);
      if (log) loadLog();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-[#C9A84C]/20 bg-black/30 p-4" data-testid={`fee-waiver-panel-${director.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-grotesk text-[8px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>Vinci Core Banking · Fee Waivers</div>
        <button type="button" data-testid={`fee-waiver-log-toggle-${director.id}`} onClick={() => (log ? setLog(null) : loadLog())} className="font-grotesk text-[8px] uppercase tracking-[0.22em] text-[#8A8A82] hover:text-[#F5F5F0]">{log ? "Hide log" : "Waiver log"}</button>
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        {PHASES.map((p) => {
          const st = phases?.[String(p.n)] || { status: "pending" };
          const waived = st.status === "waived";
          const settled = st.status === "settled";
          return (
            <div key={p.n} className={`border px-3 py-2.5 ${waived ? "border-[#C9A84C]/60 bg-[#C9A84C]/10" : "border-white/10"}`} data-testid={`fee-waiver-phase-${p.n}-${director.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="font-serif text-sm text-[#F5F5F0]">{p.label}</div>
                  <div className="font-mono text-[10px] text-[#8A8A82]">{p.amount}{settled ? " · settled by payment" : ""}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-grotesk text-[8px] uppercase tracking-[0.2em]" style={{ color: waived ? GOLD : "#8A8A82" }}>{waived ? "Waived" : "Waive fee"}</span>
                  <Switch data-testid={`fee-waiver-switch-${p.n}-${director.id}`} checked={waived} disabled={waived || settled}
                          onCheckedChange={(v) => { if (v && !waived) openDlg(p.n, "waive"); }} className="data-[state=checked]:bg-[#C9A84C]" />
                  {waived && <Lock size={11} style={{ color: GOLD }} />}
                </div>
              </div>
              {waived && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <div className="font-mono text-[9px] text-[#C9A84C]/80" data-testid={`fee-waiver-meta-${p.n}-${director.id}`}>waived {fmt(st.at)} · {st.by}</div>
                  <button type="button" data-testid={`fee-waiver-revoke-${p.n}-${director.id}`} onClick={() => openDlg(p.n, "revoke")} className="font-grotesk text-[8px] uppercase tracking-[0.2em] text-[#E5484D]/80 hover:text-[#E5484D]">Revoke · 2nd action</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
      {log && (
        <ul className="mt-3 space-y-1" data-testid={`fee-waiver-log-${director.id}`}>
          {log.length === 0 && <li className="font-body text-[11px] text-[#8A8A82]">No waiver actions recorded.</li>}
          {log.map((r, i) => (
            <li key={i} className="font-mono text-[10px] text-[#C9C9C2] flex gap-2"><span style={{ color: r.action === "waived" ? GOLD : "#E5484D" }}>{r.action.toUpperCase()}</span> Phase {r.phase} · {fmt(r.at)} · {r.by}{r.note ? ` · ${r.note}` : ""}</li>
          ))}
        </ul>
      )}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && !busy && setDlg(null)}>
        <DialogContent className="bg-[#0B0B0B] border border-[#C9A84C]/40 text-[#F5F5F0] max-w-md" data-testid="fee-waiver-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">{dlg?.mode === "revoke" ? `Revoke Phase ${dlg?.phase} waiver` : `Waive Phase ${dlg?.phase} fee`}</DialogTitle>
            <DialogDescription className="text-[#8A8A82]">
              {dlg?.mode === "revoke"
                ? "This is the second admin action required to undo a waiver. The fee becomes payable again and the revocation is logged with your reason."
                : `${director.business_name || director.name || director.email} will owe nothing for ${PHASES.find((p) => p.n === dlg?.phase)?.label}. The waiver is timestamped, logged against the director and cannot be toggled off casually.`}
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 text-sm mt-2">
            <input type="checkbox" data-testid="fee-waiver-confirm" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} className="mt-1 accent-[#C9A84C]" />
            <span>{dlg?.mode === "revoke" ? "I confirm this revocation as a deliberate second admin action." : "I confirm this fee is waived in full for this director."}</span>
          </label>
          <textarea data-testid="fee-waiver-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder={dlg?.mode === "revoke" ? "Reason (required, min 8 characters)" : "Internal note (optional)"}
                    className="w-full bg-black/60 border border-white/15 p-2 text-sm text-[#F5F5F0] focus:outline-none focus:border-[#C9A84C]" />
          <div className="flex justify-end gap-2 mt-2">
            <button type="button" onClick={() => setDlg(null)} disabled={busy} className="px-4 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] text-[#8A8A82]">Cancel</button>
            <button type="button" data-testid="fee-waiver-submit" onClick={submit} disabled={busy || !confirm || (dlg?.mode === "revoke" && note.trim().length < 8)}
                    className="inline-flex items-center gap-2 px-5 py-2 font-grotesk text-[9px] uppercase tracking-[0.24em] font-bold disabled:opacity-40"
                    style={{ background: dlg?.mode === "revoke" ? "#E5484D" : GOLD, color: "#000" }}>
              {busy ? <Loader2 size={11} className="animate-spin" /> : <ShieldCheck size={11} />} {dlg?.mode === "revoke" ? "Revoke waiver" : "Waive fee"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Director-side: gold "Fee Waived" status only — amounts are never shown.
export function FeeWaivedStatus({ phases }) {
  if (!phases) return null;
  const any = Object.values(phases).some((p) => p.status !== "pending");
  if (!any) return null;
  return (
    <div className="mt-5 flex flex-wrap items-center gap-2" data-testid="director-fee-status">
      <span className="font-grotesk text-[8px] uppercase tracking-[0.3em] text-[#8A8A82] mr-1">Vinci Core Banking</span>
      {PHASES.map((p) => {
        const st = phases[String(p.n)]?.status || "pending";
        const waived = st === "waived";
        return (
          <span key={p.n} data-testid={`director-fee-phase-${p.n}`} data-status={st}
                className="inline-flex items-center gap-2 rounded-full border px-3 py-1 font-grotesk text-[8px] uppercase tracking-[0.26em]"
                style={waived ? { color: "#FFF3D1", borderColor: GOLD, background: `${GOLD}22`, boxShadow: `0 0 10px ${GOLD}aa, 0 0 24px ${GOLD}55` }
                              : st === "settled" ? { color: "#2FB999", borderColor: "#2FB99966" } : { color: "#8A8A82", borderColor: "#ffffff22" }}>
            {waived && <ShieldCheck size={10} style={{ color: GOLD }} />}
            Phase {p.n} · {waived ? "Fee Waived" : st === "settled" ? "Settled" : "Pending"}
          </span>
        );
      })}
    </div>
  );
}
